import React, {FunctionComponent, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Colors, FormGroup, Icon, Switch} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';

import {ISharedWithUser, ReportSharingSettings, SharingSettingsTypes} from '../../../../../../models';
import {useCurrentUser} from '../../../../../../hooks';
import {shareWithUser} from '../../../../../../store/actions/sharingSettingsActions';
import {setLastInvitedUser} from '../../../../../../store/actions/userActions';
import {IState} from '../../../../../../store/reducers';
import SuggestUser from './SuggestUser';
import {UserSuggestItem} from './types';
import {getUserPermission, suggestUserOptions} from './utils';
import ListUsers from './ListUsers';

import './WithUser.scss';

interface WithUserProps {
  uid: string;
  sharingSettings: ReportSharingSettings;
}

const LabelElement = () => {
  return (
    <>
      <Icon
        icon={IconNames.PEOPLE}
        color={Colors.GRAY1}
      /> Invite individual users:
    </>
  )
}

const WithUser: FunctionComponent<WithUserProps> = ({
  uid,
  sharingSettings,
}: WithUserProps) => {
  const [selected, setSelected] = useState<ISharedWithUser[]>(sharingSettings.shared_with_users || []);

  const currentUser = useCurrentUser();
  const recipients: any = useSelector((state: IState) => state.users.recipients);
  const createdBy = useSelector((state: IState) => state.reportForm.report?.created_by);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setLastInvitedUser(null));
    return () => {
      dispatch(setLastInvitedUser(null));
    }
  }, []);

  const onSelect = (value: UserSuggestItem) => {
    if (!value.uid) {
      /* this means "Add user" was clicked */
      return;
    }

    const found = selected.find((s: ISharedWithUser) => s.uid === value.uid);

    if (!found) { /* if not found */
      /*
       * view is selected by default
       */
      const newSelected = {uid: value.uid, view: true} as ISharedWithUser;

      setSelected((oldSelected: ISharedWithUser[]) => [...oldSelected, newSelected]);
      dispatch(shareWithUser(SharingSettingsTypes.REPORT, uid, value.uid, getUserPermission(newSelected)));
    }
  };

  const onRemoveUser = (userUid: string) => {
    if (!userUid) {
      return;
    }

    setSelected(
      (oldSelected: ISharedWithUser[]) => [...oldSelected.filter((s: any) => s.uid !== userUid)]
    );
    // if permission is not specified, it will be deleted
    dispatch(shareWithUser(SharingSettingsTypes.REPORT, uid, userUid, ""));
  };

  const onChangeAction = (userUid: string, action: string) => {
    dispatch(shareWithUser(SharingSettingsTypes.REPORT, uid, userUid, action));
  };

  return (
    <div className="share-with-item share-with-user">
      <FormGroup
        className="suggest-user-input"
        label={<LabelElement/>}
      >
        <SuggestUser
          options={suggestUserOptions(recipients, selected, createdBy)}
          onSelect={onSelect}
        />
      </FormGroup>
      <ListUsers
        currentUser={currentUser}
        recipients={recipients}
        selected={selected}
        createdBy={createdBy as any}
        onRemove={onRemoveUser}
        onChangeAction={onChangeAction}
      />
    </div>
  )
};

export default WithUser;
